import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useBilling } from '../../hooks/useBilling';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { 
  Home, 
  FolderOpen, 
  MessageSquare, 
  BarChart3, 
  FileText, 
  Settings,
  CreditCard,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Zap,
  Brain,
  X
} from 'lucide-react';

// Import the logos
import CrutanLogoNavy from '../../logos/All Navy Crutan Logo.svg';
import CrutanLogoWhite from '../../logos/All White Crutan Logo.svg';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Projects', href: '/projects', icon: FolderOpen },
  { name: 'AI Agents', href: '/agents', icon: MessageSquare },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, disabled: true, comingSoon: true },
  { name: 'Reports', href: '/reports', icon: FileText, beta: true },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
  isMobileSidebarOpen?: boolean;
  setIsMobileSidebarOpen?: (open: boolean) => void;
}

export function Sidebar({ isMobileSidebarOpen, setIsMobileSidebarOpen }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [usageView, setUsageView] = useState<'api' | 'ai'>('api');
  
  // Fetch usage data
  const { useUsage } = useBilling();
  const { data: usage, isLoading: usageLoading } = useUsage();

  // Calculate usage percentages and format numbers
  const formatUsageNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const apiUsage = {
    used: usage?.currentCounts?.api_calls || 0,
    limit: usage?.limits?.max_api_calls || 2000,
    percentage: usage?.currentCounts?.api_calls && usage?.limits?.max_api_calls 
      ? Math.min(Math.round((usage.currentCounts.api_calls / usage.limits.max_api_calls) * 100), 100)
      : 0
  };

  const aiUsage = {
    used: usage?.currentCounts?.ai_tokens || 0,
    limit: usage?.limits?.max_ai_requests || 20000,
    percentage: usage?.currentCounts?.ai_tokens && usage?.limits?.max_ai_requests 
      ? Math.min(Math.round((usage.currentCounts.ai_tokens / usage.limits.max_ai_requests) * 100), 100)
      : 0
  };

  const currentUsage = usageView === 'api' ? apiUsage : aiUsage;

  const handleNavItemClick = () => {
    // Close mobile sidebar when a nav item is clicked
    if (setIsMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  };

  return (
    <div className={cn(
      'fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300',
      // Desktop: always visible with dynamic width
      'hidden lg:block',
      isCollapsed ? 'lg:w-16' : 'lg:w-64',
      // Mobile: overlay when open
      isMobileSidebarOpen ? 'block w-64' : 'lg:block'
    )}>
      {/* Mobile header with close button */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <img 
            src={theme === 'dark' ? CrutanLogoWhite : CrutanLogoNavy}
            alt="Crutan Logo"
            className="h-8"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileSidebarOpen?.(false)}
          className="h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Desktop header with collapse toggle */}
      <div className="hidden lg:flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
        {!isCollapsed && (
          <div className="flex items-center">
            <img 
              src={theme === 'dark' ? CrutanLogoWhite : CrutanLogoNavy}
              alt="Crutan Logo"
              className="h-8"
            />
          </div>
        )}
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="h-8 w-8"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="mt-6 px-3">
        <div className="space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            const isDisabled = item.disabled;
            
            if (isDisabled) {
              return (
                <div
                  key={item.name}
                  className={cn(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg cursor-not-allowed opacity-50',
                    'text-gray-400 dark:text-gray-500'
                  )}
                  title={(isCollapsed && !isMobileSidebarOpen) ? `${item.name} - Coming Soon` : undefined}
                >
                  <item.icon
                    className={cn(
                      'flex-shrink-0 h-5 w-5 text-gray-300 dark:text-gray-600',
                      (isCollapsed && !isMobileSidebarOpen) ? 'mr-0' : 'mr-3'
                    )}
                  />
                  {(!isCollapsed || isMobileSidebarOpen) && (
                    <div className="flex items-center justify-between w-full">
                      <span>{item.name}</span>
                      {item.comingSoon && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleNavItemClick}
                className={cn(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 text-blue-700 dark:text-blue-400 border-r-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                )}
                title={(isCollapsed && !isMobileSidebarOpen) ? item.name : undefined}
              >
                <item.icon
                  className={cn(
                    'flex-shrink-0 h-5 w-5 transition-colors',
                    isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400',
                    (isCollapsed && !isMobileSidebarOpen) ? 'mr-0' : 'mr-3'
                  )}
                />
                {(!isCollapsed || isMobileSidebarOpen) && (
                  <div className="flex items-center justify-between w-full">
                    <span>{item.name}</span>
                    {item.beta && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        Beta
                      </span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Quick Actions */}
      {(!isCollapsed || isMobileSidebarOpen) && (
        <div className="mt-8 px-3">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center mb-2">
              <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Quick Start</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
              Ask your first question to get insights from your data
            </p>
            <Link
              to="/agents/data"
              onClick={handleNavItemClick}
              className="inline-flex items-center justify-center w-full px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
            >
              <MessageSquare className="w-3 h-3 mr-2" />
              Start Chat
            </Link>
          </div>
        </div>
      )}

      {/* Usage Indicator */}
      {(!isCollapsed || isMobileSidebarOpen) && (
        <div className="mt-6 px-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
            {/* Usage Type Toggle */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setUsageView('api')}
                  className={cn(
                    'flex items-center px-2 py-1 rounded text-xs font-medium transition-colors',
                    usageView === 'api' 
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  )}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  API
                </button>
                <button
                  onClick={() => setUsageView('ai')}
                  className={cn(
                    'flex items-center px-2 py-1 rounded text-xs font-medium transition-colors',
                    usageView === 'ai' 
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  )}
                >
                  <Brain className="w-3 h-3 mr-1" />
                  AI
                </button>
              </div>
              {!usageLoading && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {currentUsage.percentage}%
                </span>
              )}
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2">
              <div 
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  usageView === 'api' 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700' 
                    : 'bg-gradient-to-r from-purple-600 to-purple-700'
                )} 
                style={{ width: `${Math.min(currentUsage.percentage, 100)}%` }}
              />
            </div>

            {/* Usage Text */}
            {usageLoading ? (
              <div className="animate-pulse">
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatUsageNumber(currentUsage.used)} / {formatUsageNumber(currentUsage.limit)} {usageView === 'api' ? 'calls' : 'tokens'}
              </p>
            )}

            {/* Quick link to billing */}
            <Link 
              to="/billing" 
              onClick={handleNavItemClick}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 block mt-2 font-medium"
            >
              View details →
            </Link>
          </div>
        </div>
      )}

      {/* User Info */}
      {(!isCollapsed || isMobileSidebarOpen) && (
        <div className="absolute bottom-0 w-full p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mr-3">
              <span className="text-sm font-medium text-white">
                {(user?.user_metadata?.name || user?.email)?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.user_metadata?.name || user?.email?.split('@')[0]}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}